"use client";

import { useMemo } from "react";
import {
  MessageSquare,
  TrendingUp,
  BookOpen,
  Crown,
  OctagonAlert
} from "lucide-react";
import { Card, CardContent } from "components/ui/card_v3";
import { cn } from "lib/utils";
import localization from "services/localization";

function KpiCard({ title, value, subtitle, icon: Icon, accentColor, theme }) {
  const card_bg =
    theme === "dark"
      ? "bg-[var(--card-bg)] border-border"
      : "bg-card border-border";

  return (
    <Card className={cn("overflow-hidden", card_bg)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {title}
            </p>
           <p
  className={cn(
    typeof value === "number" || !isNaN(parseFloat(value))
      ? "text-2xl font-bold"
      : "text-lg font-bold",
    "text-foreground leading-snug line-clamp-2 break-words"
  )}
  title={value}
>
  {value}
</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            )}
          </div>
          <div
            className={cn(
              "flex h-10 w-10 items-center justify-center rounded-lg",
              accentColor
            )}
          >
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function KpiCards({ messages, users, prompts, theme }) {

 const kpiData = useMemo(() => {
    const totalMessages = messages.reduce(
      (sum, m) => sum + (m.metrics?.total_messages || 0),
      0
    );

    // Unique active users
    const activeUserIds = new Set(messages.map((m) => m.user_id));
    const activeUsers = activeUserIds.size;

    // Average messages per user
    const avgPerUser = activeUsers > 0 ? (totalMessages / activeUsers).toFixed(1) : 0;

    // Most used prompt
    const promptCounts = {};
    messages.forEach((m) => {
      m.top_prompts?.forEach((p) => {
        promptCounts[p.prompt_id] = (promptCounts[p.prompt_id] || 0) + p.count;
      });
    });

    let topPromptId = null;
    let max = 0;
    for (const id in promptCounts) {
      if (promptCounts[id] > max) {
        max = promptCounts[id];
        topPromptId = id;
      }
    }

    const topPrompt = prompts.find((p) => p.id === topPromptId);
    const topPromptName = topPrompt?.title?.It || topPrompt?.title?.En || "Unknown";
    const topPromptCount = topPromptId ? promptCounts[topPromptId] : 0;

    // Average rating
    const ratingsSum = messages.reduce(
      (sum, m) =>
        sum + (m.metrics?.avg_rating || 0) * (m.metrics?.total_messages || 0),
      0
    );
    const avgRating = totalMessages > 0 ? (ratingsSum / totalMessages).toFixed(1) : 0;

    // Average Policy Risk (weighted)
    const totalRisk = messages.reduce(
      (sum, m) =>
        sum + (m.metrics?.avg_policy_risk || 0) * (m.metrics?.total_messages || 0),
      0
    );
    const avgPolicyRisk = totalMessages > 0 ? (totalRisk / totalMessages).toFixed(1) : 0;

    return { totalMessages, avgPerUser, avgRating, avgPolicyRisk, topPromptName, topPromptCount };
  }, [messages, prompts]);

  return (
        <div className="flex flex-col gap-4">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      <KpiCard
        title={localization.total_messages || "Total Messages"}
        value={kpiData.totalMessages.toLocaleString()}
        subtitle={localization.total_conversations || "Total conversations"}
        icon={MessageSquare}
        accentColor="bg-blue-500/15 text-blue-500"
        theme={theme}
      />
      <KpiCard
        title={localization.avg_rating || "Avg Rating"}
        value={`${kpiData.avgRating} / 5`}
        subtitle={localization.messages_rating_score || "Messages rating score"}
        icon={TrendingUp}
        accentColor="bg-amber-500/15 text-amber-500"
        theme={theme}
      />
      <KpiCard
        title={localization.avg_policy_risk || "Avg Policy Risk"}
        value={`${kpiData.avgPolicyRisk} / 5`}
        subtitle={localization.policy_risk_score || "Policy risk score"}
        icon={OctagonAlert}
        accentColor="bg-indigo-500/15 text-indigo-500"
        theme={theme}
      />

       <KpiCard
        title={localization.top_prompt || "Top prompt"}
        value={kpiData.topPromptName}
        subtitle={`Used ${kpiData.topPromptCount} times`}
        icon={Crown}
        accentColor="bg-emerald-500/15 text-emerald-500"
        theme={theme}
      /> 
          </div>
      
      </div>
  );
}
